import { useRef } from 'react';

import { CircleDot, X } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useRegisterShortcut } from '@erp-core/shortcuts';

import { useWorkspaceStore, type WorkspaceTab } from '@stores/workspace-store';

import { cn } from '@shared/utils/cn';

import styles from './ERPWorkspace.module.scss';

// ─── Component ────────────────────────────────────────────────────────────────

export function ERPWorkspace() {
  const tabs = useWorkspaceStore((s) => s.tabs);
  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const setActiveTab = useWorkspaceStore((s) => s.setActiveTab);
  const closeTab = useWorkspaceStore((s) => s.closeTab);
  const navigate = useNavigate();
  const tabBarRef = useRef<HTMLDivElement>(null);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────

  // Ctrl+W — close the active tab
  useRegisterShortcut({
    id: 'workspace:close-tab',
    combo: { key: 'w', modifiers: ['ctrl'] },
    scope: 'global',
    handler: () => {
      const active = tabs.find((t) => t.id === activeTabId);
      if (active?.closeable) {
        handleCloseTab(active.id);
      }
    },
    meta: { label: 'Close current tab', group: 'Workspace' },
  });

  // Ctrl+Tab — next tab
  useRegisterShortcut({
    id: 'workspace:next-tab',
    combo: { key: 'tab', modifiers: ['ctrl'] },
    scope: 'global',
    handler: () => {
      if (tabs.length < 2) return;
      const idx = tabs.findIndex((t) => t.id === activeTabId);
      const next = tabs[(idx + 1) % tabs.length];
      if (next) navigateToTab(next);
    },
    meta: { label: 'Next tab', group: 'Workspace' },
  });

  // Ctrl+Shift+Tab — previous tab
  useRegisterShortcut({
    id: 'workspace:prev-tab',
    combo: { key: 'tab', modifiers: ['ctrl', 'shift'] },
    scope: 'global',
    handler: () => {
      if (tabs.length < 2) return;
      const idx = tabs.findIndex((t) => t.id === activeTabId);
      const prev = tabs[(idx - 1 + tabs.length) % tabs.length];
      if (prev) navigateToTab(prev);
    },
    meta: { label: 'Previous tab', group: 'Workspace' },
  });

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function navigateToTab(tab: WorkspaceTab): void {
    setActiveTab(tab.id);
    navigate(tab.path);
    // Scroll tab into view
    requestAnimationFrame(() => {
      const el = tabBarRef.current?.querySelector<HTMLElement>(`[data-tab-id="${tab.id}"]`);
      el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  function handleCloseTab(tabId: string, e?: React.MouseEvent): void {
    e?.stopPropagation();
    e?.preventDefault();

    const idx = tabs.findIndex((t) => t.id === tabId);
    const tab = tabs[idx];
    if (!tab) return;

    // If closing the active tab, navigate to the adjacent one first
    if (tabId === activeTabId) {
      const next =
        tabs[idx + 1] ??
        tabs[idx - 1] ??
        (tabs.length > 1 ? (tabs.find((t) => t.id !== tabId) ?? null) : null);

      if (next) {
        navigate(next.path);
        setActiveTab(next.id);
      } else {
        navigate('/dashboard');
      }
    }

    closeTab(tabId);
  }

  const location = useLocation();
  const isBookingModule = location.pathname.startsWith('/booking');

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.workspace}>
      {/* ── Tab bar (only shown in booking module) ────────────────────────── */}
      {tabs.length > 0 && isBookingModule && (
        <div
          ref={tabBarRef}
          role="tablist"
          aria-label="Open workspaces"
          id="erp-workspace-tabs"
          className={styles.tabBar}
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`${tab.title}${tab.isDirty ? ' (unsaved changes)' : ''}`}
                data-tab-id={tab.id}
                className={cn(styles.tab, isActive && styles.tabActive)}
                onClick={() => navigateToTab(tab)}
              >
                {/* Dirty indicator */}
                {tab.isDirty && (
                  <span className={styles.dirtyDot} aria-hidden="true">
                    <CircleDot size={8} />
                  </span>
                )}

                <span className={styles.tabTitle}>{tab.title}</span>

                {/* Close button */}
                {tab.closeable && (
                  <span
                    role="button"
                    tabIndex={0}
                    className={styles.closeBtn}
                    onClick={(e) => handleCloseTab(tab.id, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCloseTab(tab.id);
                      }
                    }}
                    aria-label={`Close ${tab.title}`}
                  >
                    <X size={12} aria-hidden="true" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <main
        className={styles.content}
        role="main"
        id="erp-main-content"
        tabIndex={-1}
        aria-label="Page content"
      >
        {/* Skip link target */}
        <a href="#erp-main-content" className={styles.skipLink}>
          Skip to content
        </a>
        <Outlet />
      </main>
    </div>
  );
}
